import React from 'react';

import isEmpty from 'lodash/isEmpty';
import without from 'lodash/without';

import {
  LegacyBox,
  MultiSelect,
  LegacyText,
  PublishIcon,
  DownloadIcon,
  Actions,
  Action,
} from '@gogame/components';
import { useChooseFiles } from '@gogame/hooks-ts';

import NodesBudgetGrid from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/NodesBudgetGrid';
import { useGroup } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Group';
import { useLoader } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Loader';
import { useViewStateByResolveName } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/ViewState';
import {
  useCreateNodesMarketingPlan,
  useUploadNodesMarketingPlan,
  useExportNodesMarketingPlan,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/hooks';

import { Header, Title, EmptyMessageBox } from './components';

const NodesBudget = ({
  levelName,
  subLevelList = [],
  addNodesActionLabel = '',
  withLinkName,
  withSplitBy,
  withRemove,
  withUploadNode,
  withDownloadNode,
  withCBudgetEmptyPlaceholder,
}) => {
  const { id, nodes, depth, activityTypes } = useViewStateByResolveName();
  const { levelPath } = useGroup();
  const { processAsync } = useLoader();
  const createNodes = useCreateNodesMarketingPlan(processAsync);
  const uploadNodes = useUploadNodesMarketingPlan(processAsync);
  const exportNodes = useExportNodesMarketingPlan();

  const entityIds = React.useMemo(
    () => nodes.map(({ entityId }) => entityId),
    [nodes]
  );

  const handleAddNodes = React.useCallback(
    (_e, value) => {
      createNodes({
        parentId: id,
        depth: depth + 1,
        entityIds: without(value, ...entityIds),
        levelPath,
        node: { activityTypes },
      });
    },
    [id, depth, entityIds, activityTypes]
  );

  const chooseSpreadsheet = useChooseFiles({ multiple: false, accept: '.csv' });

  const handleUploadNodes = React.useCallback(async () => {
    const file = await chooseSpreadsheet();
    uploadNodes({ file, parentId: id, levelPath });
  }, [id]);

  const handleExportNodes = React.useCallback(() => {
    exportNodes({ parentId: id });
  }, [id]);

  return (
    <React.Fragment>
      <Header gutter={8} align={'center'}>
        <LegacyBox grow={1}>
          <Title>{`Split by ${levelName}`}</Title>
        </LegacyBox>
        <Actions>
          {withUploadNode ? (
            <Action
              label={'Import'}
              icon={<PublishIcon />}
              onClick={handleUploadNodes}
            />
          ) : null}
          {withDownloadNode ? (
            <Action
              label={'Export'}
              icon={<DownloadIcon />}
              onClick={handleExportNodes}
            />
          ) : null}
        </Actions>
        {subLevelList.length ? (
          <MultiSelect
            width={'200px'}
            label={addNodesActionLabel}
            onChange={handleAddNodes}
            options={subLevelList}
            value={entityIds}
            canDeselect={false}
            hideSelectedOption={true}
            markSelectedOption={true}
          />
        ) : null}
      </Header>
      {isEmpty(nodes) ? (
        <EmptyMessageBox justifyContent={'center'}>
          <LegacyText>{`No ${levelName} selected`}</LegacyText>
        </EmptyMessageBox>
      ) : (
        <NodesBudgetGrid
          levelName={levelName}
          withLinkName={withLinkName}
          withSplitBy={withSplitBy}
          withRemove={withRemove}
          withCBudgetEmptyPlaceholder={withCBudgetEmptyPlaceholder}
        />
      )}
    </React.Fragment>
  );
};

export default NodesBudget;
